import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Iconify from 'src/components/iconify';
import './ContributionTopbar.css';

const STORAGE_KEY = 'eyhe_contribution_topbar_hidden_until';
const REOPEN_DELAY = 5 * 60 * 1000;

const CONVERSATION_PATHS = [
  '/dashboard/chat',
  '/dashboard/video',
  '/dashboard/audio',
  '/dashboard/chamadaaudio',
  '/dashboard/chamadavideo',
  '/dashboard/salaespera',
  '/dashboard/conversar',
  '/dashboard/pagamento',
  '/dashboard/verperfil',
  '/dashboard/agendamento',
  '/dashboard/reagendamento',
];

type Props = {
  layout: 'horizontal' | 'mini' | 'vertical';
  onHeightChange: (height: number) => void;
};

function getHiddenUntil() {
  const value = Number(sessionStorage.getItem(STORAGE_KEY) || 0);
  return Number.isFinite(value) ? value : 0;
}

export default function ContributionTopbar({ layout, onHeightChange }: Props) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const barRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(() => getHiddenUntil() <= Date.now());
  const isConversationFlow = CONVERSATION_PATHS.some((path) => pathname.startsWith(path));
  const shouldRender = visible && !isConversationFlow;

  useEffect(() => {
    const hiddenUntil = getHiddenUntil();

    if (hiddenUntil <= Date.now()) {
      sessionStorage.removeItem(STORAGE_KEY);
      setVisible(true);
      return undefined;
    }

    setVisible(false);
    const timer = window.setTimeout(() => {
      sessionStorage.removeItem(STORAGE_KEY);
      setVisible(true);
    }, hiddenUntil - Date.now());

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!shouldRender || !barRef.current) {
      onHeightChange(0);
      return undefined;
    }

    const bar = barRef.current;
    const updateHeight = () => onHeightChange(Math.ceil(bar.getBoundingClientRect().height));
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(bar);

    return () => {
      observer.disconnect();
      onHeightChange(0);
    };
  }, [onHeightChange, shouldRender]);

  const scrollToContribution = useCallback(() => {
    let attempts = 0;
    const findAndScroll = () => {
      const contribution = document.getElementById('contribuicao-dashboard');

      if (contribution) {
        contribution.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      attempts += 1;
      if (attempts < 30) {
        window.setTimeout(findAndScroll, 100);
      }
    };

    if (pathname !== '/dashboard/dashfirst') {
      navigate('/dashboard/dashfirst');
    }

    window.setTimeout(findAndScroll, 50);
  }, [navigate, pathname]);

  function closeTopbar() {
    const hiddenUntil = Date.now() + REOPEN_DELAY;
    sessionStorage.setItem(STORAGE_KEY, String(hiddenUntil));
    setVisible(false);

    window.setTimeout(() => {
      sessionStorage.removeItem(STORAGE_KEY);
      setVisible(true);
    }, REOPEN_DELAY);
  }

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      ref={barRef}
      className={`eyhe-contribution-topbar eyhe-contribution-topbar-${layout}`}
      role="region"
      aria-label="Pedido de contribuição para o Eyhe"
    >
      <button
        type="button"
        className="eyhe-contribution-close"
        onClick={closeTopbar}
        aria-label="Fechar aviso por 5 minutos"
      >
        <Iconify icon="eva:close-fill" width={20} />
      </button>

      <div className="eyhe-contribution-inner">
        <div className="eyhe-contribution-art" aria-hidden="true">
          <svg width="72" height="72" viewBox="0 0 168 128" fill="none">
            <path d="M20 78c-2-16 8-30 24-32 8-1 14 3 17 9l6 13-14 22c-9 6-20 4-27-3-5-3-6-6-6-9z" fill="#DCE8FF" />
            <path d="M148 78c2-16-8-30-24-32-8-1-14 3-17 9l-6 13 14 22c9 6 20 4 27-3 5-3 6-6 6-9z" fill="#DCE8FF" />
            <g className="eyhe-contribution-pulse">
              <path d="M84 46c-9-13-29-9-29 6 0 15 20 26 29 33 9-7 29-18 29-33 0-15-20-19-29-6z" fill="#FF8552" />
            </g>
            <path d="M32 34l2.4 5.6L40 42l-5.6 2.4L32 50l-2.4-5.6L24 42l5.6-2.4z" fill="#FF8552" opacity="0.55" />
            <path d="M140 30l1.8 4.2L146 36l-4.2 1.8L140 42l-1.8-4.2L134 36l4.2-1.8z" fill="#2F6FED" opacity="0.5" />
          </svg>
        </div>

        <div className="eyhe-contribution-content">
          <span className="eyhe-contribution-badge">
            <span aria-hidden="true" />
            +50 mil pessoas já foram acolhidas pelo Eyhe
          </span>
          <h2>Ajude o Eyhe a continuar salvando vidas</h2>
          <p>Sua contribuição mantém a plataforma no ar e leva escuta e acolhimento para mais pessoas.</p>
        </div>

        <button type="button" className="eyhe-contribution-cta" onClick={scrollToContribution}>
          Quero contribuir
        </button>
      </div>
    </div>
  );
}
